import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Edit2,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { format } from 'date-fns'
import { supabase, isDemoMode } from '@/lib/supabase'
import { DEMO_ACTIVITY_INSTANCES } from '@/lib/demoData'
import { useFilters } from '@/lib/store'
import { useAuth } from '@/hooks/useAuth'
import { getDisciplineInfo, getProgressColor } from '@/lib/constants'
import StatusBadge from '@/components/shared/StatusBadge'
import { ProgressCell } from '@/components/shared/ProgressBar'
import type { ActivityInstance, DisciplineType } from '@/types'

// =============================================================================
// ActivityTable Component
// =============================================================================

const PAGE_SIZE = 30

type SortKey = 'parcel' | 'family' | 'discipline' | 'progress' | 'status' | 'updated'
type SortDir = 'asc' | 'desc'

export default function ActivityTable() {
  const { filters }  = useFilters()
  const { canEdit }  = useAuth()
  const queryClient  = useQueryClient()

  const [page, setPage]         = useState(0)
  const [sortKey, setSortKey]   = useState<SortKey>('parcel')
  const [sortDir, setSortDir]   = useState<SortDir>('asc')
  const [editingId, setEditing] = useState<string | null>(null)
  const [editPct, setEditPct]   = useState<number>(0)

  // ---------------------------------------------------------------------------
  // Fetch activity instances
  // ---------------------------------------------------------------------------
  const { data, isLoading } = useQuery({
    queryKey: ['activities', filters, page, sortKey, sortDir],
    queryFn: async () => {
      if (isDemoMode) {
        let items = [...DEMO_ACTIVITY_INSTANCES]
        if (filters.status)  items = items.filter(i => i.status === filters.status)
        if (!filters.showGlobals) items = items.filter(i => i.parcel_id !== null)
        if (sortKey === 'progress') items.sort((a, b) => sortDir === 'asc' ? (a.progress_pct ?? 0) - (b.progress_pct ?? 0) : (b.progress_pct ?? 0) - (a.progress_pct ?? 0))
        const start = page * PAGE_SIZE
        return { items: items.slice(start, start + PAGE_SIZE) as ActivityInstance[], total: items.length }
      }
      let query = supabase
        .from('activity_instances')
        .select(`
          *,
          family:activity_families(code, name, discipline),
          parcel:parcels(parcel_number, display_name),
          assignments:activity_assignments(
            is_active,
            company:companies(name)
          )
        `, { count: 'exact' })
        .eq('is_active', true)
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      // Apply filters
      if (filters.parcels.length > 0) {
        // Join-based filter approximation
      }
      if (filters.status) {
        query = query.eq('status', filters.status)
      }
      if (!filters.showGlobals) {
        query = query.not('parcel_id', 'is', null)
      }

      // Sort
      const sortMap: Record<SortKey, string> = {
        parcel:     'parcel_id',
        family:     'family_id',
        discipline: 'family_id',
        progress:   'progress_pct',
        status:     'status',
        updated:    'updated_at',
      }
      query = query.order(sortMap[sortKey], { ascending: sortDir === 'asc' })

      const { data, count, error } = await query
      if (error) throw error
      return { items: (data ?? []) as ActivityInstance[], total: count ?? 0 }
    },
    staleTime: 30_000,
  })

  // ---------------------------------------------------------------------------
  // Inline edit mutation
  // ---------------------------------------------------------------------------
  const editMutation = useMutation({
    mutationFn: async ({ id, pct }: { id: string; pct: number }) => {
      if (isDemoMode) return  // no-op in demo
      const { error } = await supabase
        .from('activity_instances')
        .update({
          progress_pct: pct,
          status:       pct >= 100 ? 'FINALIZADA' : pct > 0 ? 'EN_CURSO' : 'NO_INICIADA',
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] })
      setEditing(null)
    },
  })

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(0)
  }

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown className="w-3 h-3 text-gray-300" />
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-brand-600" />
      : <ChevronDown className="w-3 h-3 text-brand-600" />
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="card overflow-hidden flex flex-col">
      {/* Table */}
      <div className="overflow-x-auto flex-1">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('parcel')}
                  >
                    Parcela <SortIcon col="parcel" />
                  </button>
                </th>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('family')}
                  >
                    Familia <SortIcon col="family" />
                  </button>
                </th>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('discipline')}
                  >
                    Disciplina <SortIcon col="discipline" />
                  </button>
                </th>
                <th>Subcontratista</th>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('progress')}
                  >
                    % Reportado <SortIcon col="progress" />
                  </button>
                </th>
                <th>% Validado</th>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('status')}
                  >
                    Estado <SortIcon col="status" />
                  </button>
                </th>
                <th>
                  <button
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => handleSort('updated')}
                  >
                    Actualizado <SortIcon col="updated" />
                  </button>
                </th>
                {canEdit && <th>Acción</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                    No se encontraron actividades con los filtros aplicados
                  </td>
                </tr>
              ) : (
                items.map((inst) => {
                  const family     = inst.family as { code: string; name: string; discipline: string } | null
                  const parcel     = inst.parcel as { parcel_number: number; display_name: string } | null
                  const assignment = (inst.assignments as Array<{ is_active: boolean; company: { name: string } }> | null)
                    ?.find((a) => a.is_active)
                  const discInfo   = family ? getDisciplineInfo(family.discipline as DisciplineType) : null
                  const isEditing  = editingId === inst.id

                  return (
                    <tr
                      key={inst.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        inst.status === 'BLOQUEADA' ? 'bg-red-50/30' :
                        inst.status === 'FINALIZADA' ? 'bg-blue-50/20' : ''
                      }`}
                    >
                      {/* Parcel */}
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-gray-700">
                          {parcel?.display_name ?? 'Global'}
                        </span>
                      </td>

                      {/* Family */}
                      <td className="px-4 py-3">
                        <div className="max-w-[180px]">
                          <p className="text-xs font-medium text-gray-800 truncate">
                            {family?.code}. {family?.name}
                          </p>
                        </div>
                      </td>

                      {/* Discipline */}
                      <td className="px-4 py-3">
                        {discInfo && (
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{ backgroundColor: discInfo.bgColor, color: discInfo.color }}
                          >
                            {discInfo.label}
                          </span>
                        )}
                      </td>

                      {/* Subcontractor */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600">
                          {assignment?.company?.name ?? <span className="text-gray-300">—</span>}
                        </span>
                      </td>

                      {/* Reported % */}
                      <td className="px-4 py-3 w-32">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={editPct}
                            onChange={(e) => setEditPct(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                            className="w-20 text-xs border border-brand-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
                            autoFocus
                          />
                        ) : (
                          <ProgressCell value={inst.progress_pct} />
                        )}
                      </td>

                      {/* Validated % */}
                      <td className="px-4 py-3 w-32">
                        <ProgressCell value={inst.validated_pct} />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={inst.status} size="sm" dot />
                      </td>

                      {/* Updated */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-gray-400">
                          {format(new Date(inst.updated_at), 'dd/MM/yy HH:mm')}
                        </span>
                      </td>

                      {/* Actions */}
                      {canEdit && (
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => editMutation.mutate({ id: inst.id, pct: editPct })}
                                disabled={editMutation.isPending}
                                className="p-1 rounded text-green-600 hover:bg-green-50"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setEditing(null)}
                                className="p-1 rounded text-gray-400 hover:bg-gray-100"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditing(inst.id)
                                setEditPct(inst.progress_pct)
                              }}
                              className="p-1 rounded text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-500">
          {total > 0
            ? `Mostrando ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} de ${total} actividades`
            : 'Sin resultados'
          }
        </p>
        <div className="flex items-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-600 font-medium">
            {page + 1} / {totalPages || 1}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
